/**
 * SectionNav - in-page section navigation (click → scroll to the section).
 *
 * Unlike `Tabs`, it does NOT hide/show panels - all sections are on the same page
 * one below another, the nav only scrolls to the right place and highlights the active one (scroll-spy).
 * Used the same way both in admin PlanPage (vertical, left sticky) and
 * account BillingSection (horizontal, top sticky).
 *
 * The sections' DOM elements must carry an `id` (the same as `items[].id`) and
 * a `scroll-mt-*` class (so the sticky nav does not cover the heading when scrolling).
 */
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";

export interface SectionNavItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

/** Scroll to the section (smoothly). `scroll-mt-*` on the section keeps room clear of the sticky nav. */
export function scrollToSection(id: string): void {
  if (typeof document === "undefined") return;
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Scroll-spy: watch which `ids` section is near the top of the viewport, return
 * its id (for highlighting the active one). `rootMargin` forms an „active band"
 * near the upper third. Re-attaches when `ids` change.
 */
export function useScrollSpy(ids: string[], rootMargin = "-15% 0px -75% 0px"): string {
  const [active, setActive] = useState(ids[0] ?? "");
  const key = ids.join(",");
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    let io: IntersectionObserver | null = null;

    /** Attach the observer once ALL/some sections are in the DOM. Return whether it succeeded. */
    const attach = (): boolean => {
      const els = ids.map((id) => document.getElementById(id)).filter((e): e is HTMLElement => e != null);
      if (els.length === 0) return false;
      io = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setActive(visible[0].target.id);
        },
        { rootMargin, threshold: 0 },
      );
      els.forEach((el) => io!.observe(el));
      return true;
    };

    // Sections may be missing on the first render (e.g. the parent is still loading data).
    // If they are not there, wait with a MutationObserver until they appear in the DOM, then attach.
    if (attach()) return () => io?.disconnect();
    const mo = new MutationObserver(() => {
      if (attach()) mo.disconnect();
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); io?.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, rootMargin]);
  return active;
}

export interface SectionNavProps {
  items: SectionNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
  orientation?: "horizontal" | "vertical";
  className?: string;
  /** Accessible name of the <nav> landmark. The kit has no translations of its
   *  own, so the default is English. */
  ariaLabel?: string;
}

export function SectionNav({
  items, activeId, onSelect, orientation = "horizontal", className, ariaLabel = "Section navigation",
}: SectionNavProps) {
  const horizontal = orientation === "horizontal";
  return (
    <nav
      className={cn(
        horizontal ? "flex gap-1 overflow-x-auto" : "flex flex-col gap-1",
        className,
      )}
      aria-label={ariaLabel}
    >
      {items.map((it) => {
        const active = it.id === activeId;
        const Icon = it.icon;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onSelect(it.id)}
            aria-current={active ? "true" : undefined}
            className={cn(
              "flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              horizontal ? "shrink-0" : "w-full text-left",
              active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {Icon && <Icon className="size-4 shrink-0" />}
            <span>{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
